import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  RendererFactory2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DocumentFieldEmitterService } from 'app/global/documentfield-emitter.service';
import { DownloadFileService } from 'app/global/download-file.service';
import { GlobalService } from 'app/global/global.service';
import { IntegrationService } from 'app/global/integrations/integrations.service';
import { Field } from 'app/interfaces/fields';
import { MomentPipe } from 'app/pipes/moment.pipe';
import { combineLatest, Observable, of, Subject, Subscription, take } from 'rxjs';
import { catchError, concatMap, debounceTime, delay, filter, map, switchMap, takeUntil, tap, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { FieldData, TemplateForm } from '../../edit-template/edit-template.dto';
import {
  AbstractUpdateFieldsComponent
} from '../../general-editor-tabs/fields-builder-service/abstract-update-fields-data.component';
import { FieldsBuilderService } from '../../general-editor-tabs/fields-builder-service/fields-builder.service';
import { DocumentTypeEnum } from '../../templates/template.dto';
import { DocumentIndexDataModalComponent } from '../document-index-data-modal/document-index-data-modal.component';
import { DocumentPermissions, DocumentsPermissionsService } from '../documents-permissions.service';
import { DocumentsService } from '../documents.service';
import { DocumentInternalReminder, IDocumentDetailRootDTO } from '../dto/document-details.dto';
import { DocumentStatusesEnum } from '../dto/document-statuses.enum';
import { InternalReminderModalComponent } from '../modals/internal-reminder-modal/internal-reminder-modal.component';
import { SendReminderToAllComponent } from '../modals/send-reminder-to-all/send-reminder-to-all.component';
import { DocumentListingFacade } from '../store/document-listing.facade';
import { DocumentHelperFacade } from './document-helper.facade';
import { documentActions } from './document-rightbar-menu';
import { FieldsDataHelperService } from '../../general-editor-tabs/fields-builder-service/fields-data-helper.service';
import { AddonKeys } from '../../addons/addons/addon-keys.enum';
import {
  IPlaybookListFacadeServiceToken
} from '../../legal-hub/playbooks/playbook-list/plyabbok-guideline-run-tile/playbook-list.facade.service';
import { DocumentsRightbarPlaybookListFacadeService } from './documents-rightbar-playbook-list.facade.service';
import { Actions, ofType } from '@ngrx/effects';
import * as DocumentListingActions from '../store/document-listing.actions';
import {
  getReminderDateText,
  getReminderMessageText,
  getReminderUsersText,
  getReminders,
  hasReminder,
  isReminderExpired
} from '../utils/document-reminder.utils';

export enum DocumentRightSidebarTabs {
  DETAILS = 'details',
  PARTICIPANTS = 'participants',
  ACTIVITIES = 'activities',
  CONTENT_CREATOR = 'content-creator',
  LEGAL_HUB = 'legal-hub'
}

@Component({
  selector: 'app-document-right-sidebar',
  templateUrl: './document-right-sidebar.component.html',
  styleUrls: ['./document-right-sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class DocumentRightSidebarComponent extends AbstractUpdateFieldsComponent implements OnDestroy, OnInit {

  @ViewChild('tooltip') tooltip: NgbTooltip;
  @ViewChild('documentNameInput') documentNameInput: ElementRef;


  protected DocumentRightSidebarTabs = DocumentRightSidebarTabs;

  public activeTab = DocumentRightSidebarTabs.DETAILS;
  public documentData$ = this._documentsFacade.selectedDocument$;
  public selectedDocument$ = this._documentsFacade.selectedDocument$
  public lastSelectedDocument$ = this._documentsFacade.lastSelectedDocument$;

  public documentDataLoader$ = this._documentsFacade.documentDataLoader$;
  public documentData: IDocumentDetailRootDTO;
  public clearButton: HTMLElement = document.createElement('button');
  public canSoftDeleteDocument$: Observable<boolean>;
  public canDeleteDocumentPermanently$: Observable<boolean>;
  private timerSubscription: Subscription;
  public documentStatusesEnum = DocumentStatusesEnum;
  public isDocumentNameEditable: boolean = false;
  public resendLoader: boolean = false;
  public destroy$ = new Subject<void>();
  public documentFieldsData = {};

  public documentStatuses = [
    DocumentStatusesEnum.Sent,
    DocumentStatusesEnum.Draft,
    DocumentStatusesEnum.Uploaded,
    DocumentStatusesEnum.Expired,
    DocumentStatusesEnum.Revoke,
    DocumentStatusesEnum.Approved,
    DocumentStatusesEnum.Signed,
    DocumentStatusesEnum.Viewed,
    DocumentStatusesEnum.Completed
  ];

  public rightBarMenuItems = []; // Document right bar three dot menu actions
  public documentPermissions$: DocumentPermissions = null;
  public specialAction: string = null;
  public isResendingToMetodika: boolean = false;
  private checkedDocumentIds: Set<number> = new Set(); // Cache for checked document IDs
  public canChangeStatus$: Observable<boolean> = combineLatest({
  status: this.documentData$.pipe(map(document => document?.template?.status ?? null)),
  changedManually: this.lastSelectedDocument$.pipe(
    filter(document => document !== null),
    map(document => document?.status_updated_manually === 1)),
  changedIfTemplateDocument: this.lastSelectedDocument$.pipe(
    filter(document => document !== null),
    map(document => document?.type === DocumentTypeEnum.TEMPLATEDOCUMENT))
  })
  .pipe(map(({ status, changedManually, changedIfTemplateDocument }) =>
    status === DocumentStatusesEnum.Uploaded || changedManually || changedIfTemplateDocument
  ));

  protected AddonKeys = AddonKeys;

  /**
   * Initializes the component and subscribes to document data and permissions.
   *
   * This constructor sets up the right bar menu items and subscribes to various observables
   * to populate the document fields data and update the document permissions.
   *
   * @param _coreSidebarService
   * @param modalService
   * @param _documentsFacade
   * @param _documentService
   * @param _globalService
   * @param _downloadFileService
   * @param router
   * @param _translocoService
   * @param _documentHelperFacade$
   * @param _documentFacade$
   * @param _documentPermissionsService
   * @param _documentFieldsEmitter
   * @param _momentPipe$
   * @param rendererFactory
   * @param _integrationService
   * @param _modalService
   * @param _fieldsBuilderService
   * @param _fieldsDataHelperService
   * @param documentsRightbarPlaybookListFacadeService
   */
  constructor(
    private _coreSidebarService: CoreSidebarService,
    private modalService: NgbModal,
    private _documentsFacade: DocumentListingFacade,
    private _documentService: DocumentsService,
    public _globalService: GlobalService,
    private _downloadFileService: DownloadFileService,
    private router: Router,
    public _translocoService: TranslocoService,
    private _documentFacade$: DocumentListingFacade,
    private _documentPermissionsService: DocumentsPermissionsService,
    public _documentFieldsEmitter: DocumentFieldEmitterService,
    public _momentPipe$: MomentPipe,
    public rendererFactory: RendererFactory2,
    public _integrationService: IntegrationService,
    public _modalService: NgbModal,
    public _fieldsBuilderService: FieldsBuilderService,
    public _documentHelperFacade$: DocumentHelperFacade,
    public _fieldsDataHelperService: FieldsDataHelperService,
    private _actions$: Actions,
    @Inject(IPlaybookListFacadeServiceToken) public documentsRightbarPlaybookListFacadeService: DocumentsRightbarPlaybookListFacadeService
  ) {

    super(
      _globalService,
      _documentFieldsEmitter,
      _momentPipe$,
      rendererFactory,
      _integrationService,
      _modalService,
      _fieldsBuilderService,
      _translocoService,
      _documentHelperFacade$,
      _fieldsDataHelperService
    );


    this.rightBarMenuItems = documentActions(this);

    this.initDocumentPermissionsStream();
    this.initDocumentDetailStream();
  }


  ngOnInit(): void {
    this._actions$
      .pipe(
        ofType(DocumentListingActions.documentClicked),
        filter(({ documentId }) => !!documentId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.activeTab = DocumentRightSidebarTabs.DETAILS;
      });

    this.initFieldsUpdateStream();
    this.selectedDocument$
      .pipe(
        map(doc => doc?.template?.id),
        filter(docId => !!docId),
        distinctUntilChanged(), // Only emit when the document ID changes
        filter(docId => !this.checkedDocumentIds.has(docId)), // Only check if not cached
        takeUntil(this.destroy$),
        tap(docId => {
          this.checkedDocumentIds.add(docId);
          this._documentsFacade.checkSpecialDocumentActions(docId);
        })
      )
      .subscribe();

    this._documentsFacade.specialAction$
      .pipe(
        filter(action => !!action),
        takeUntil(this.destroy$)
      )
      .subscribe(action => {
        this.specialAction = action;
      });

    this._actions$.pipe(
      ofType(DocumentListingActions.resendToMetodikaSuccess, DocumentListingActions.resendToMetodikaFailure),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isResendingToMetodika = false;
    });

  }





  /**
   * Initializes the document permissions stream.
   * 
   * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
   * It then subscribes to the stream and updates the document permissions.
   * 
   * @returns {void}
   */
  private initDocumentPermissionsStream(): void {
    this.selectedDocument$
      .pipe(
        filter(doc => !!doc),
        takeUntil(this.destroy$),
        switchMap(() => this._documentPermissionsService.getPermissions())
      )
      .subscribe(permissions => {
        this.documentPermissions$ = permissions;
      });
  }

  sendToMetodikaAgain(): void {
    if (this.isResendingToMetodika) {
      return;
    }
    this.isResendingToMetodika = true;
    this.selectedDocument$
      .pipe(take(1))
      .subscribe((doc) => {
        this._documentsFacade.resendToMetodika(doc.template.id);
      }
      );
  }



  /**
   * Initializes the document detail stream.
   * 
   * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
   * It then subscribes to the stream and updates the fields data.
   * 
   * @returns {void}
   */
  private initDocumentDetailStream(): void {
    this.selectedDocument$
      .pipe(
        filter(doc => !!doc),
        takeUntil(this.destroy$)
      )
      .subscribe((documentData: IDocumentDetailRootDTO) => {
        const documentDetail = structuredClone(documentData);
        this._globalService.fields_data = structuredClone(documentDetail?.template_fields_datas?.data ?? {});
        this.fields_data = this.extractFieldsData()?.filter((field: Field) => !field.hidden && !field.secret_hidden_field);
      });
  }





  /**
   * Initializes a reactive stream that listens for updates to the selected document's fields.
   * 
   * This method subscribes to changes in the selected document and, for each document,
   * listens for field update triggers from the document helper facade. When an update is triggered,
   * it debounces the event, filters out invalid parameters, and processes the update if the current
   * route does not include 'send-document'. It updates the fields data in the state and also updates
   * the fields data with only the relevant fields based on the provided UUIDs, including any deleted field UUIDs.
   *
   * @private
   * @remarks
   * - Uses RxJS operators for reactive stream management.
   * - Ensures cleanup with `takeUntil(this.destroy$)`.
   * - Updates both the global state and filtered fields data in the documents facade.
   */
  private initFieldsUpdateStream(): void {
    this.selectedDocument$
      .pipe(
        map(doc => doc?.template?.id),
        filter(docId => !!docId),
        takeUntil(this.destroy$),
        switchMap(docId =>
          this._documentHelperFacade$
            .subscribeToShouldUpdateFieldsData()
            .pipe(
              debounceTime(400),
              filter(params => !!params),
              map(params => ({ id: docId, params }))
            )
        )
      )
      .subscribe(({ id, params }) => {
        if (!this.router?.url?.includes('send-document')) {
          const payload = {
            dataable_type: 'document',
            dataable_id: id,
            field_type: 'document_field',
            deleted_field_uuid: params.deleted_field_uuid,
            data: this._globalService.fields_data as FieldData[]
          };

          const filteredFields = Object.values(this._globalService.fields_data)
            .filter((field: Field) => params.uuid.includes(field.uuid))
            .reduce((acc, field: Field) => {
              const relatedFields = Object.values(this._globalService.fields_data)
                .filter((f: Field) => f.selected_field_type_id === field.selected_field_type_id && f.field_section === field.field_section);

              if (relatedFields?.length) {
                relatedFields.forEach((relatedField: Field) => {
                  acc[relatedField.uuid] = relatedField;
                });
              }
              return acc;
            }, {} as Record<string, FieldData>);
          this._documentsFacade.updateFieldsData(
            {
              ...payload,
              deleted_field_uuid: params.deleted_field_uuid,
              filteredFields: filteredFields as FieldData[]
            }
          );
        }
      });
  }





  /**
   * Opens the Document Index Data Modal.
   * 
   * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
   * It then opens a modal using `modalService` with the `DocumentIndexDataModalComponent` component.
   * The modal is configured with a large size and a static backdrop.
   * The `documentId` property of the modal's component instance is set to the `id` of the template from the emitted data.
   * 
   * @returns {void}
   */
  openIndexDataModal(): void {
    this.selectedDocument$.pipe(
      take(1)
    ).subscribe(data => {
      const instance = this.modalService.open(DocumentIndexDataModalComponent, {
        size: 'lg',
        backdrop: 'static',
      });
      instance.componentInstance.documentId = data?.template?.id
    })
  }




  /**
   * Enables the document name input field for editing and sets focus on it after a short delay.
   * 
   * This method sets `isDocumentNameEditable` to `true` to allow editing of the document name.
   * It then uses an observable to delay for 300 milliseconds before setting focus on the 
   * `documentNameInput` element.
   * 
   * @returns {void}
   */
  toggleEditAbleInput(): void {
    if (!this.documentPermissions$?.hasFullAccessToDocument || !this.documentPermissions$?.canEditDocument) return

    this.isDocumentNameEditable = true;
    this.timerSubscription = of(null).pipe(
      take(1),
      delay(300)
    ).subscribe(() => {
      this.documentNameInput.nativeElement.focus();
    });
  }




  /**
   * Updates the document name if it has changed.
   * 
   * This method listens for a change event on the document name input field,
   * retrieves the current document data, and updates the document name if it
   * differs from the existing name. After updating, it sets the document name
   * input field to non-editable.
   * 
   * @param $event - The event object from the input field, containing the new document name.
   * @returns {void}
   */
  updateDocumentName($event): void {
    this.selectedDocument$.pipe(
      take(1)
    ).subscribe(result => {
      const documentName = $event?.target?.value;
      if (result?.template?.name != documentName) {
        this._documentsFacade.updateDocumentName(result?.template?.id, documentName);
      }
      this.isDocumentNameEditable = false;
    });
  }




  /**
   * Updates the document status.
   * 
   * This method takes a `status` parameter and updates the document status using the
   * `updateDocumentStatus` method from the `_documentsFacade` service.
   * 
   * @param status - The new status for the document.
   * @returns {void}
   */
  updateDocumentStatus(status: DocumentStatusesEnum): void {
    this.selectedDocument$.pipe(
      take(1),
      map(response => {
        this._documentsFacade.updateDocumentStatus(response?.template?.id, status);
      })
    ).subscribe();
  }




  /**
   * Toggles the sidebar with the given name.
   *
   * @param name - The name of the sidebar to toggle.
   * @returns {void}
   */
  toggleSidebar(name: string): void {
    const sidebar = this._coreSidebarService.getSidebarRegistry(name);
    if (!sidebar) {
      console.warn(`Sidebar registry for "${name}" not found`);
      return;
    }
    if (typeof sidebar.toggleOpen !== 'function') {
      console.warn('Sidebar registry found but toggleOpen is not a function', sidebar);
      return;
    }
    sidebar.toggleOpen();
  }




  /**
    * Opens the Send Reminder to All Modal.
    * 
    * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
    * It then opens a modal using `modalService` with the `SendReminderToAllComponent` component.
    * The `documentData` property of the modal's component instance is set to the emitted data.
    * 
    * The modal's result is then subscribed to, and if a result is returned, the Send Reminder to All
    * is updated using the `onUpdateInternalReminder` method from the `_documentFacade$` service.
    * 
    * @returns {void}
    */
  SendRemindertoAll(): void {
    this.selectedDocument$.pipe(take(1)).subscribe(documentData => {
      const modalRef$ = this.modalService.open(SendReminderToAllComponent);
      modalRef$.componentInstance.documentData = documentData;
    });
  }



  /**
    * Opens the Internal Reminder Modal.
    * 
    * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
    * It then opens a modal using `modalService` with the `InternalReminderModalComponent` component.
    * The `documentData` property of the modal's component instance is set to the emitted data.
    * 
    * The modal's result is then subscribed to, and if a result is returned, the internal reminder
    * is updated using the `onUpdateInternalReminder` method from the `_documentFacade$` service.
    * 
    * @returns {void}
    */
  internalReminderClick(): void {
    this.selectedDocument$.pipe(take(1)).subscribe(documentData => {
      const modalRef$ = this.modalService.open(InternalReminderModalComponent);
      modalRef$.componentInstance.documentData = documentData;
      modalRef$.result.then(result => {
        if (result) {
          this._documentFacade$.onUpdateInternalReminder(result?.document_internal_reminder);
        }
      });
    });
  }




  /**
   * Opens a SweetAlert2 confirmation dialog to confirm the revocation of a document.
   * If the user confirms, it triggers the document revocation process.
   * 
   * The confirmation dialog includes:
   * - A title translated using the Transloco service.
   * - A warning icon.
   * - A confirmation button with translated text and a red color.
   * - A cancel button with translated text.
   * 
   * If the user confirms the action, the document data is retrieved using an observable,
   * and the document revocation is initiated by calling the `documentRevoke` method
   * from the `_documentsFacade` service with the document's template ID.
   */
  revokeDocument(): void {
    Swal.fire({
      title: this._translocoService.translate('User confirmation (general) - Are you sure'),
      text: this._translocoService.translate('Ask to revoke document (Documents Section) - You want to revoke this document'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this._translocoService.translate('Document listview - reject document modal buttons - yes'),
      confirmButtonColor: '#ef0016',
      cancelButtonText: this._translocoService.translate('Document listview - reject document modal buttons - no'),
    }).then(async (result) => {
      if (result.value) {
        this.selectedDocument$.pipe(
          take(1),
        ).subscribe(data => {
          this._documentsFacade.documentRevoke(data?.template?.id);
        })
      }
    });
  }




  /**
   * Opens the document in full screen mode.
   * 
   * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
   * It then uses the `_documentHelperFacade$` service to redirect the document to full screen mode.
   * 
   * @returns {void}
   */
  openFullScreen(): void {
    this.selectedDocument$.pipe(take(1)).subscribe(response => this._documentHelperFacade$.redirectToFullScreen(response));
  }




  /**
   * Permanently deletes a document after user confirmation.
   * 
   * This method subscribes to the document data observable, prompts the user
   * with a confirmation modal, and if the user confirms, it triggers the 
   * deletion of the document.
   * 
   * @returns {void}
   */
  deleteDocumentPermanently(): void {
    this.selectedDocument$
      .pipe(take(1))
      .subscribe(async (doc) => {
        const message = this._translocoService.translate('Ask to delete document (Documents Section) - Do you want to delete this document permanently');
        const { isConfirmed } = await this.confirmDeleteDocumentModal(message);

        if (isConfirmed) {
          this._documentsFacade.deletePermanently(doc?.template?.id);
        }
      });
  }



  /**
   * Moves the current document to the trash after user confirmation.
   * 
   * This method subscribes to the `selectedDocument$` observable and retrieves the document data.
   * It then prompts the user with a confirmation modal to confirm if they want to move the document to the trash.
   * If the user confirms, the document is soft deleted using the `_documentsFacade`.
   * 
   * @returns {void}
   */
  moveToTrash(): void {
    this.selectedDocument$
      .pipe(take(1))
      .subscribe(async (doc) => {
        const message = this._translocoService.translate('Ask to trash document (Documents Section) - Do you want to move this document to the trash');
        const { isConfirmed } = await this.confirmDeleteDocumentModal(message);
        if (isConfirmed) {
          this._documentsFacade.softDelete(doc?.template?.id);
        }
      });
  }



  /**
   * Opens a SweetAlert2 confirmation dialog to confirm the deletion of a document.
   * 
   * This method takes a `confirmationText` parameter and opens a SweetAlert2 confirmation dialog
   * with the provided text. The dialog includes:
   * - A title translated using the Transloco service.
   * - A warning icon.
   * - A confirmation button with translated text and a red color.
   * - A cancel button with translated text.
   * 
   * The method returns a promise with the result of the confirmation dialog.
   * 
   * @param confirmationText - The text to display in the confirmation dialog.
   * @returns {Promise<SweetAlertResult<unknown>>} - A promise with the result of the confirmation dialog.
   */
  private async confirmDeleteDocumentModal(confirmationText: string): Promise<import("sweetalert2").SweetAlertResult<unknown>> {
    return await Swal.fire({
      title: this._translocoService.translate('User confirmation (general) - Are you sure'),
      text: confirmationText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this._translocoService.translate('Modal button (general) - Yes'),
      confirmButtonColor: '#ef0016',
      cancelButtonText: this._translocoService.translate('Modal button (general) - No'),
    });
  }



  /**
   * Resends the document to the recipients.
   * 
   * This method sets the `resendLoader` property to `true` to indicate that the document is being resent.
   * It then subscribes to the `selectedDocument$` observable and retrieves the document data.
   * The method filters the template roles to exclude the sender and then resends the document using the `_documentService`.
   * 
   * If the document is successfully resent, the method navigates to the send document page for the updated document.
   * If an error occurs during the resend process, the `resendLoader` property is set to `false` and an error message is displayed.
   * 
   * @returns {void} 
   */
  resendDocument(): void {
    this.resendLoader = true;
    this.selectedDocument$.pipe(
      take(1),
    ).subscribe(result => {
      const templateRoles = result.template_roles.filter((role) => role?.recipient_role_detail?.is_sender != 1);
      const templateData = { ...result, template_roles: templateRoles }
      this._documentService.resendDocument(result.template.id, templateData).subscribe({
        next: async (document: TemplateForm) => {
          this.router.navigate([`/send-document/${document.template.id}`]);
        }, error: (error: string) => {
          this.resendLoader = false;
          this._globalService.toastrError(error);
        }
      });
    });
  }




  /**
   * Downloads the document as a PDF file.
   * 
   * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
   * It then downloads the PDF file using the `_documentService` and the `downloadPDF` method.
   * If the download is successful, the file is saved using the `_downloadFileService`.
   * If an error occurs during the download process, an error message is displayed.
   * 
   * @returns {void}
   */
  downloadPDF(): void {
    this.selectedDocument$.pipe(
      take(1),
      concatMap(data => this._documentService.downloadPDF(data?.template?.id).pipe(
        tap((blob: Blob) => {
          const fileName = data?.template_details?.visible_name
            ? data?.template_details?.visible_name
            : data?.template?.name;
          this._downloadFileService.downloadFile(blob, fileName);
        }),
        catchError(error => {
          this._globalService.toastrError(error);
          return of(null);
        })
      ))
    ).subscribe();
  }




  /**
   * Edits the document.
   * 
   * This method subscribes to the `selectedDocument$` observable and takes the first emitted value.
   * It then navigates to the send document page for the document template.
   * 
   * @returns {void}
   **/
  editDocument() {
    this.selectedDocument$.pipe(
      take(1),
    ).subscribe(result => {
      this.router.navigate([`send-document/${result.template.id}`], { state: { template_id: result.template.id } });
    });
  }

  onLegalHubSuggestionClose(): void {
    this.documentsRightbarPlaybookListFacadeService.showSuggestions$.next(false);
    this.documentsRightbarPlaybookListFacadeService.guideLineSuggestions$.next(null);
  }

  onActiveNavIdChange(): void {
    this.onLegalHubSuggestionClose();
  }

  /**
   * Unsubscribes from the timer subscription when the component is destroyed.
   * 
   * This method checks if the `timerSubscription` property is set and unsubscribes from it if it is.
   * 
   * @returns {void}
   */
  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.checkedDocumentIds.clear();
    this.destroy$.next();
    this.destroy$.complete();
    this.onLegalHubSuggestionClose();
  }

  changeTab(tabName: DocumentRightSidebarTabs): void {
    this.activeTab = tabName;
  }

  hasReminder(document: IDocumentDetailRootDTO): boolean {
    return hasReminder(document);
  }

  isReminderExpired(document: IDocumentDetailRootDTO): boolean {
    return isReminderExpired(document);
  }

  getReminders(document: IDocumentDetailRootDTO): DocumentInternalReminder[] {
    return getReminders(document);
  }

  getReminderUsersText(reminder: DocumentInternalReminder): string {
    return getReminderUsersText(reminder);
  }

  getReminderMessageText(reminder: DocumentInternalReminder): string {
    return getReminderMessageText(reminder);
  }

  getReminderDateText(reminder: DocumentInternalReminder): string {
    return getReminderDateText(reminder, this._translocoService.getActiveLang());
  }

}
